import React, { useEffect, useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";

import {
    httpRequest,
    httpUpload,
    onRequestError,
    printLog,
} from "../../helper/global";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ImageSelector, {
    openImagePickerDialog,
} from "../ImageSelector/ImageSelector";

export default function TinyMCE({
    theme,
    initialContent = "",
    onContentChange = null,
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const editorRef = useRef(null);

    const [text, setText] = useState("");
    const [imageSelector, setImageSelector] = useState(false);

    const [imageList, setImageList] = useState([]);

    const image_upload_handler = (blobInfo, progress) =>
        new Promise(async (resolve, reject) => {
            const formData = new FormData();
            formData.append("image", blobInfo.blob(), blobInfo.filename());

            try {
                const result = await httpUpload(
                    `upload/emailImage`,
                    formData,
                    "POST",
                    (error) => {
                        onRequestError(dispatch, navigate, {
                            title: error.title,
                            message: error.message,
                        });
                        resolve("");
                    }
                );

                if (result === false) {
                    resolve("");
                    return;
                }

                resolve(result.payload);
                fetchImageList();
            } catch (err) {
                console.log("catch", err.message);
                reject(`Invalid: ${err.message}`);
            }
        });

    const fetchImageList = async () => {
        try {
            const result = await httpRequest(
                `upload/imageList`,
                {},
                "GET",
                (error) => {
                    onRequestError(dispatch, navigate, {
                        title: error.title,
                        message: error.message,
                    });
                }
            );

            if (result === false) {
                return;
            }

            setImageList(result.payload);
        } catch (error) {
            console.error("Error fetching email lists:", error);
        }
    };

    useEffect(() => {
        setText(initialContent);
    }, [initialContent]);

    return (
        <>
            <Editor
                key={"tinymce-editor"}
                apiKey={`${process.env.REACT_APP_TINYMCE_API_KEY}`}
                onInit={(_evt, editor) => (editorRef.current = editor)}
                value={text}
                onEditorChange={(newValue, editor) => {
                    setText(newValue);
                    onContentChange(newValue);
                }}
                init={{
                    skin: theme.tinyMCE.skin,
                    content_css: theme.tinyMCE.content_css,
                    height: "100%",
                    menubar: true,
                    plugins: [
                        "advlist",
                        "lists",
                        "link",
                        "autolink",
                        "image",
                        "preview",
                        "code",
                        "table",
                    ],
                    file_picker_types: "image",
                    image_uploadtab: true,
                    images_upload_handler: image_upload_handler,

                    toolbar: `undo redo | blocks | 
                                    fontfamily fontsize bold italic forecolor | alignleft aligncenter 
                                    alignright alignjustify | bullist numlist outdent indent | table link image | 
                                    removeformat | code | preview`,
                    content_style:
                        "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                    file_picker_callback: function (cb, value, meta) {
                        // Ensure this picker is for images

                        if (meta.filetype === "image") {
                            fetchImageList();
                            setImageSelector(true);
                            openImagePickerDialog(cb);
                        }
                    },
                    setup: (editor) => {
                        // Robust DOM-based linkifier: converts http(s):// and www. links inside pasted HTML,
                        // skipping text already inside anchors.
                        const linkifyHtml = (html) => {
                            try {
                                const div = document.createElement("div");
                                div.innerHTML = html;

                                // Match full URLs with protocol or starting with www.
                                const urlRegex =
                                    /(https?:\/\/[^\s<]+)|(www\.[^\s<]+)/gi;

                                const walker = document.createTreeWalker(
                                    div,
                                    NodeFilter.SHOW_TEXT,
                                    null,
                                    false
                                );

                                const textNodes = [];
                                let node;
                                while ((node = walker.nextNode())) {
                                    // Skip if this text node is already inside an anchor
                                    if (
                                        node.parentElement &&
                                        node.parentElement.closest("a")
                                    ) {
                                        continue;
                                    }
                                    if (urlRegex.test(node.nodeValue)) {
                                        textNodes.push(node);
                                    }
                                }

                                textNodes.forEach((textNode) => {
                                    const text = textNode.nodeValue;
                                    urlRegex.lastIndex = 0;
                                    let lastIndex = 0;
                                    const frag =
                                        document.createDocumentFragment();
                                    let match;
                                    while (
                                        (match = urlRegex.exec(text)) !== null
                                    ) {
                                        const url = match[0];
                                        const idx = match.index;
                                        if (idx > lastIndex) {
                                            frag.appendChild(
                                                document.createTextNode(
                                                    text.slice(lastIndex, idx)
                                                )
                                            );
                                        }
                                        const a = document.createElement("a");
                                        a.href = url.startsWith("http")
                                            ? url
                                            : `https://${url}`;
                                        a.target = "_blank";
                                        a.rel = "noopener noreferrer";
                                        a.textContent = url;
                                        frag.appendChild(a);
                                        lastIndex = idx + url.length;
                                    }
                                    if (lastIndex < text.length) {
                                        frag.appendChild(
                                            document.createTextNode(
                                                text.slice(lastIndex)
                                            )
                                        );
                                    }
                                    textNode.parentNode.replaceChild(
                                        frag,
                                        textNode
                                    );
                                });

                                return div.innerHTML;
                            } catch (err) {
                                return html;
                            }
                        };

                        editor.on("PastePostProcess", function (e) {
                            e.node.innerHTML = linkifyHtml(e.node.innerHTML);
                        });
                    },
                }}
            />
            <ImageSelector
                open={imageSelector}
                onClose={() => {
                    setImageSelector(false);
                }}
                imageList={imageList}
            />
        </>
    );
}
